import { PlusIcon } from "@heroicons/react/24/outline";
import { Button, Chip, Divider, Stack, Typography } from "@mui/joy";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { IfElse } from "../../../../components/Condition";
import showErrorToast, { ErrorToastConfig } from "../../../../components/Error";
import { theme } from "../../../../components/Theme";
import PaymentOptions from "../../../../components/payments/PaymentOptions";
import { AddressService } from "../../../../services/address.service";
import { CatalogType } from "../../../../types/catalog";
import { IAddressResponse } from "../../../../types/profile";
import { formatAddress } from "../../../../utils/address";
import { getCountryInfo } from "../../../../utils/country";
import AddressModal from "../../../Profile/Address/AddressModal";
import useFormContainer from "../../useFormContainer";
import { PaymentFlowTypeEnum } from "../../../../types/order";
import AddressSelectionModal from "../AddressSelectionModal";
import { useFeeStore } from "./store";

type Props = {
  catalog: CatalogType;
};

const FinalizeOrderSection = ({ catalog }: Props) => {
  const { t } = useTranslation();

  const [addresses, setAddresses] = useState<IAddressResponse[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<IAddressResponse>();
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showNewAddressModal, setShowNewAddressModal] = useState(false);

  const methods = useFormContext();

  const {
    getValues,
    setValue,
    control,
    formState: { isValid },
  } = methods;

  const { handleValidateStep } = useFormContainer(methods, catalog);

  const {
    deliveryFee,
    taxFee,
    subtotal,
    setPaymentFee,
    subtotalMultiPayment,
    discount,
  } = useFeeStore();

  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const res = await AddressService.getAddressByAccount();
        setAddresses(res);

        // Find default address or fallback to first address
        const defaultAddress = res.find((add) => add.defaultAddress) ?? res[0];

        if (defaultAddress) {
          setValue("address", defaultAddress);
          setSelectedAddress(defaultAddress);
        }
      } catch (error) {
        console.error("Failed to fetch addresses:", error);
        toast(showErrorToast(error), ErrorToastConfig);
      }
    };

    fetchAddress();
  }, []);

  return (
    <Stack direction={"column"} gap={"24px"}>
      {/* address section */}
      <Stack
        direction={"column"}
        sx={{
          gap: "16px",
          p: "16px",
          border: `1px solid ${theme.palette.neutral[300]}`,
          borderRadius: "8px",
        }}
      >
        <Typography level="title-md" textColor={theme.palette.primary[500]}>
          {t("catalog.checkout.steps.3.1")}
        </Typography>

        {/* address typography */}
        <Stack
          direction={"column"}
          sx={{
            gap: "8px",
            mt: "10px",
          }}
        >
          {selectedAddress ? (
            <AddressTypography address={selectedAddress} />
          ) : (
            <Typography
              level="text-sm-regular"
              textColor={theme.palette.danger[500]}
            >
              {t("catalog.checkout.noAddressPrompt")}
            </Typography>
          )}
        </Stack>

        {/* buttons */}
        <Stack
          direction={"row"}
          sx={{
            gap: "16px",
          }}
        >
          <IfElse
            condition={addresses.length > 0}
            ifBlock={
              <>
                <Button
                  variant="linkz-outlined-primary"
                  sx={{
                    minWidth: "144px",
                    maxWidth: { xs: "100%", md: "156px" },
                  }}
                  onClick={() => {
                    setShowAddressModal(true);
                  }}
                >
                  {t(
                    `catalog.checkout.${
                      getValues("address") ? "changeAddressBtn" : "setAdressBtn"
                    }`
                  )}
                </Button>

                <Button
                  variant="linkz-outlined-primary"
                  sx={{
                    minWidth: "144px",
                    maxWidth: { xs: "100%", md: "160px" },
                  }}
                  startDecorator={<PlusIcon width={20} height={20} />}
                  onClick={() => {
                    setShowNewAddressModal(true);
                  }}
                >
                  {t("profile.address.newAddress")}
                </Button>
              </>
            }
            elseBlock={
              <Button
                variant="linkz-outlined-primary"
                sx={{
                  minWidth: "144px",
                  maxWidth: { xs: "100%", md: "144px" },
                }}
                onClick={() => {
                  setShowNewAddressModal(true);
                }}
              >
                {t(`catalog.checkout.setAdressBtn`)}
              </Button>
            }
          />
        </Stack>
      </Stack>

      {/* payment section */}
      {catalog.paymentFlowType !== PaymentFlowTypeEnum.FULL_AFTER_DELIVERY && (
        <Stack
          direction={"column"}
          sx={{
            gap: "16px",
            p: "16px",
            border: `1px solid ${theme.palette.neutral[300]}`,
            borderRadius: "8px",
            display: { xs: "none", md: "flex" },
          }}
        >
          <Typography level="title-md" textColor={theme.palette.primary[500]}>
            {t("catalog.checkout.steps.3.2")}
          </Typography>

          <Typography
            level="text-sm-medium"
            textColor={theme.palette.neutral[600]}
          >
            {t("catalog.checkout.steps.3.2.subtitle")}
          </Typography>

          {/* payment options section */}
          <PaymentOptions
            control={control}
            setValue={setValue}
            onlinePaymentOnly={catalog.catalogType === "PUBLIC"}
            paymentAmount={
              catalog.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
                ? parseFloat(subtotalMultiPayment.toString())
                : parseFloat(subtotal.toString()) +
                  parseFloat(deliveryFee.toString()) +
                  parseFloat(taxFee.toString()) -
                  parseFloat(discount.toString())
            }
            paymentCurrency={catalog?.currency}
            handleChangePaymentFee={setPaymentFee}
            applicableAdminFee={catalog.business.applicableAdminFee}
            sellerId={catalog?.business.id}
            sellerCurrency={
              getCountryInfo(catalog?.business.companyCountry ?? "ID", "LOCALE")
                ?.currency
            }
          />
        </Stack>
      )}

      {/* buttons */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          display: { xs: "none", md: "flex" },
        }}
      >
        {/* pay now btn */}
        <Button
          id="public-checkout-paynow-btn"
          sx={{
            maxWidth: { xs: "100%", lg: "120px" },
            ml: "auto",
          }}
          disabled={!isValid || !handleValidateStep(3) || !selectedAddress}
          type={"submit"}
        >
          {catalog?.paymentFlowType !== PaymentFlowTypeEnum.FULL_AFTER_DELIVERY
            ? t("catalog.checkout.paynowBtn")
            : "Place Order"}
        </Button>
      </Stack>

      {/* modals */}
      {addresses.length > 0 && selectedAddress && (
        <AddressSelectionModal
          show={showAddressModal}
          onClose={() => {
            setShowAddressModal(false);
          }}
          onConfirm={(address: string) => {
            const foundAddress = addresses.find((add) => add.id === address);

            if (foundAddress) {
              setValue("address", foundAddress);
              setSelectedAddress(foundAddress);
              setShowAddressModal(false);
            }
          }}
          addresses={addresses}
          defaultAddress={selectedAddress}
        />
      )}

      <AddressModal
        show={showNewAddressModal}
        onClose={() => {
          setShowNewAddressModal(false);
        }}
        onSuccess={() => {
          window.location.reload();
        }}
      />
    </Stack>
  );
};

const AddressTypography = ({ address }: { address: IAddressResponse }) => {
  const { t } = useTranslation();

  return (
    <>
      {/* address title */}
      <Stack direction={"row"} sx={{ gap: "8px", alignItems: "center" }}>
        <Typography level="text-md-medium">{address.title}</Typography>

        {address.defaultAddress && (
          <Chip
            size="sm"
            variant="outlined"
            sx={{
              color: theme.palette.primary[500],
              borderColor: theme.palette.primary[500],
            }}
          >
            {t("commons.default")}
          </Chip>
        )}
      </Stack>

      {/* fullName & phoneNumber */}
      <Stack direction={"row"} sx={{ gap: "8px", alignItems: "center" }}>
        <Typography level="text-md-bold" textColor={theme.palette.common.black}>
          {address.title}
        </Typography>

        <Divider orientation="vertical" />

        <Typography level="text-sm-medium">{`+${address.phoneNumberCountryCode} ${address.phoneNumber}`}</Typography>
      </Stack>

      {/* address & address2 */}
      <Stack
        direction={"column"}
        sx={{
          width: { xs: "100%", md: "480px" },
        }}
      >
        <Typography
          level="text-sm-regular"
          textColor={theme.palette.neutral[600]}
        >
          {formatAddress(address)}
        </Typography>
      </Stack>
    </>
  );
};

export default FinalizeOrderSection;
